import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import Header from "../../global/Header";
import { ReactComponent as GroupLogo } from "../../data/svg/6334.svg";
import { ReactComponent as AnalyticsLogo } from "../../data/svg/6A.svg";
import { ReactComponent as AcademyLogo } from "../../data/svg/6334Academy.svg";
import Heading from "../../components/Heading";
import Essay from "../../components/Essay";
import ButtonBar from "../../components/ButtonBar";
import TextDisplay from "../../components/TextDisplay";
import SocialMediaBar from "../../components/SocialMediaBar";
import Section from "../../components/Section";
import InquiryForm from "../../components/InquiryForm";
import BookMeeting from "../../components/BookMeeting";
import Map from "../../components/Map";
import FAQs from "../../components/FAQs";
import { customColour } from "../../data/colour";
import {
  isTabWidth,
  contactPicture,
  contactIntroHeading,
  contactIntroEssay,
  contactDetailsHeading,
  contactFAQsHeading,
  contactHeaderButtons,
  light,
  dark,
  faqData,
  siteKey,
  menuItems,
} from "../../data/database";
import Footer from "../../global/Footer";

const linkLogo = [
  {
    label: "Academy",
    LinkLogoSvg: AcademyLogo,
    link: "/academy/home",
  },
  {
    label: "Analytics",
    LinkLogoSvg: AnalyticsLogo,
    link: "/analytics/home",
  },
];

const contactDetails = [
  "+234 802 318 7712",
  "867, Durosimi Abokunwa Street, Omole Phase II, Lagos, Nigeria.",
  "fisayo@6334group.net",
];

const GroupContact = ({ offsetRem = 3 }) => {
  const { hash } = useLocation();
  const isTab = useMediaQuery(isTabWidth);

  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        // Convert `offsetRem` to pixels (assuming 1rem = 16px, modify as needed)
        const offsetInPixels =
          offsetRem *
          parseFloat(getComputedStyle(document.documentElement).fontSize);

        // Calculate the position to scroll to, factoring in the offset
        const yPosition =
          element.getBoundingClientRect().top + window.scrollY - offsetInPixels;

        // Scroll to the calculated position
        window.scrollTo({ top: yPosition, behavior: "smooth" });
      }
    }
  }, [hash, offsetRem]);

  return (
    <>
      {/* HERO SECTION */}
      <Box
        sx={{
          position: "relative",
          height: `${isTab ? "40vh" : "65vh"}`,
          overflow: "hidden",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundImage: `${contactPicture}`,
        }}
      >
        <Box
          sx={{
            width: "80%",
            position: "absolute",
            top: `${isTab ? "30%" : "40%"}`,
            left: "50%",
            color: customColour.white,
            transform: "translate(-50%, 0)",
          }}
        >
          <Heading
            heading={contactIntroHeading}
            fontSize={isTab ? "1.25rem" : "3rem"}
            fontweight={isTab ? "700" : "200"}
          />
          <Essay
            paragraphs={contactIntroEssay}
            textAlign={isTab ? "center" : "center"}
            fontSize={isTab ? "0.9rem" : "1rem"}
            marginTop={isTab ? "0.25rem" : "1rem"}
          />
          <Box sx={{ width: `${isTab ? "100%" : "50%"}`, margin: "auto" }}>
            <ButtonBar
              buttons={contactHeaderButtons}
              fontSize={isTab ? "0.9rem" : "1rem"}
              gap={isTab ? "0.75rem" : "2rem"}
              background="linear-gradient(
              to right bottom,
              rgba(0, 0, 0, 0.9),
              rgba(0, 0, 0, 0.7)
              )"
            />
          </Box>
        </Box>
      </Box>

      {/* CONTACT Dark */}
      <Section
        image={dark}
        color={customColour.white}
        width={isTab ? "90%" : "60%"}
        padding={isTab ? "2.5rem" : "5rem"}
      >
        <Heading
          heading={contactDetailsHeading}
          fontSize={isTab ? "1.25rem" : "3rem"}
          fontweight={isTab ? "700" : "200"}
        />
        <TextDisplay
          textArray={contactDetails}
          margin={isTab ? "2.5rem" : "5rem"}
          fontSize={isTab ? "0.9rem" : "1rem"}
          fontWeight="100"
        />
        <SocialMediaBar />
      </Section>

      {/* INQUIRY Light */}
      <Box
        sx={{
          backgroundColor: customColour.lightBackground,
          marginTop: `${isTab ? "2.5rem" : "5rem"}`,
          marginBottom: `${isTab ? "2.5rem" : "5rem"}`,
        }}
      >
        <InquiryForm
          siteKey={siteKey}
          id="inquiry"
          title="Make an Inquiry"
          fontSize={isTab ? "1.25rem" : "3rem"}
          gridTemplateColumns={isTab ? "1fr" : "1fr 1fr"}
          maxWidth={isTab ? "100%" : "600px"}
          gap={isTab ? "0px" : "20px"}
          textFieldFontSize={isTab ? "0.9rem" : "1rem"}
          textFieldMargin={isTab ? "0.25rem" : "0.5rem"}
          backgroundImage={`url(${light})`}
          dropdownBackground={customColour.lightBackground}
        />
      </Box>

      {/* FAQs Dark */}
      <Section
        id="faqs"
        image={dark}
        color={customColour.white}
        width={isTab ? "90%" : "60%"}
        padding={isTab ? "2.5rem" : "5rem"}
      >
        <Heading
          heading={contactFAQsHeading}
          fontSize={isTab ? "1.25rem" : "3rem"}
          fontweight={isTab ? "700" : "200"}
        />
        <FAQs faqs={faqData} fontSize={isTab ? "0.9rem" : "1rem"} />
      </Section>

      {/* BOOK A MEETING Light */}
      <Box
        sx={{
          backgroundColor: customColour.lightBackground,
          marginTop: `${isTab ? "2.5rem" : "5rem"}`,
          marginBottom: `${isTab ? "2.5rem" : "5rem"}`,
        }}
      >
        <BookMeeting
          siteKey={siteKey}
          id="consult"
          title="Book a Meeting"
          fontSize={isTab ? "1.25rem" : "3rem"}
          gridTemplateColumns={isTab ? "1fr" : "1fr 1fr"}
          maxWidth={isTab ? "100%" : "600px"}
          gap={isTab ? "0px" : "20px"}
          textFieldFontSize={isTab ? "0.9rem" : "1rem"}
          textFieldMargin={isTab ? "0.25rem" : "0.5rem"}
          backgroundImage={`url(${light})`}
          dropdownBackground={customColour.lightBackground}
        />
      </Box>
    </>
  );
};

export default GroupContact;
